import type React from 'react';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from '../../../../utils/classNames';

interface AddElementSlotInputProps {
    isTemplateRootSlot: boolean;
    isSlotActive: boolean;
    showOpenMenuButton: boolean;
    buttonLabel: string;
    inputValue: string;
    isSlashInsertionTargetVisible: boolean;
    showInsertHereAboveInput: boolean;
    onOpenMenuClick: () => void;
    onTemplateRootNode: (node: HTMLButtonElement | null) => void;
    onTextareaNode: (node: HTMLTextAreaElement | null) => void;
    templateRootReferenceProps: React.ButtonHTMLAttributes<HTMLButtonElement>;
    draftTextareaReferenceProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
}

export function AddElementSlotInput({
    isTemplateRootSlot,
    isSlotActive,
    showOpenMenuButton,
    buttonLabel,
    inputValue,
    isSlashInsertionTargetVisible,
    showInsertHereAboveInput,
    onOpenMenuClick,
    onTemplateRootNode,
    onTextareaNode,
    templateRootReferenceProps,
    draftTextareaReferenceProps,
}: AddElementSlotInputProps): JSX.Element {
    if (isTemplateRootSlot) {
        return (
            <div className="flex w-full justify-center">
                <button
                    ref={onTemplateRootNode}
                    type="button"
                    className={classNames(
                        'rounded border border-slate-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600 shadow-sm transition hover:border-slate-400 hover:text-slate-800',
                        !isSlotActive && 'pointer-events-none opacity-0'
                    )}
                    {...templateRootReferenceProps}
                >
                    {buttonLabel}
                </button>
            </div>
        );
    }

    return (
        <div className="relative">
            <div className="flex items-start gap-2">
                {showOpenMenuButton ? (
                    <button
                        type="button"
                        aria-label={buttonLabel}
                        title={buttonLabel}
                        onClick={onOpenMenuClick}
                        tabIndex={isSlotActive ? 0 : -1}
                        className={classNames(
                            'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 shadow-sm transition hover:border-slate-400 hover:text-slate-700',
                            !isSlotActive && 'pointer-events-none opacity-0'
                        )}
                    >
                        <FontAwesomeIcon icon={faPlus} className="h-3 w-3" aria-hidden={true} />
                    </button>
                ) : null}
                <textarea
                    ref={onTextareaNode}
                    value={inputValue}
                    aria-label={buttonLabel}
                    placeholder={gettext('REPORT_BUILDER_ADD_ELEMENT_PLACEHOLDER')}
                    rows={1}
                    className={classNames(
                        'w-full resize-none overflow-hidden bg-transparent px-0 py-1 text-left text-sm leading-6 text-slate-700 outline-none transition placeholder:text-slate-400',
                        !isSlotActive && 'pointer-events-none opacity-0'
                    )}
                    {...draftTextareaReferenceProps}
                />
            </div>
            {isSlashInsertionTargetVisible ? (
                <div
                    aria-hidden={true}
                    className={classNames(
                        'pointer-events-none absolute left-0 right-0 flex items-center gap-2',
                        showInsertHereAboveInput
                            ? '-top-1.5 -translate-y-full'
                            : '-bottom-1.5 translate-y-full'
                    )}
                >
                    <div className="h-0 flex-1 border-t-2 border-dashed border-sky-300" />
                    <span className="whitespace-nowrap text-[11px] font-medium text-sky-700">
                        {gettext('REPORT_BUILDER_INSERT_HERE')}
                    </span>
                    <div className="h-0 flex-1 border-t-2 border-dashed border-sky-300" />
                </div>
            ) : null}
        </div>
    );
}
